import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, FileText, ArrowUpRight, Eye, ChevronDown, ChevronUp, Send, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtNOK, fmtCompact, fmtDate, relTime, daysUntil, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageContainer, PageHeader, StatusChip, StageBadge, EmptyState, Skeleton, Kpi, KpiStrip, Segmented, SearchField, CompanyLogo, InlineError } from "@/components/common";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "sent", label: "Awaiting decision" },
  { key: "draft", label: "Drafts" },
  { key: "accepted", label: "Accepted" },
  { key: "superseded", label: "History" },
];

const STATUS_RANK = { sent: 0, draft: 1, accepted: 2, superseded: 3 };

function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function cleanTitle(q) {
  const t = q.title || q.opportunity_name || "";
  const acc = (q.account_name || "").trim();
  const stripped = acc && t.toLowerCase().startsWith(acc.toLowerCase()) ? t.slice(acc.length).replace(/^[\s\-–—:]+/, "") : t;
  return (stripped || t).replace(/\s*[-–—]\s*Proposal$/i, "");
}

function ValidityBar({ q }) {
  if (q.status !== "sent" || !q.valid_until) return null;
  const start = q.sent_at ? new Date(q.sent_at).getTime() : null;
  const end = new Date(q.valid_until).getTime();
  const left = daysUntil(q.valid_until);
  const total = start ? Math.max(1, end - start) : null;
  const pct = total ? Math.min(100, Math.max(0, ((Date.now() - start) / total) * 100)) : 0;
  const urgent = left !== null && left <= 7;
  return (
    <div className="mt-2.5 flex items-center gap-3" data-testid="quotes-row-validity">
      <div className="h-1 w-full max-w-[220px] overflow-hidden rounded-full bg-black/[0.06]">
        <div className={cn("h-full rounded-full", urgent ? "bg-[hsl(var(--status-warn))]" : "bg-foreground/55")} style={{ width: `${pct}%` }} />
      </div>
      <span className={cn("whitespace-nowrap text-[11.5px] tabular", urgent ? "font-medium text-[hsl(var(--status-warn))]" : "text-muted-foreground")}>
        {left === null ? "" : left < 0 ? "Validity expired" : `${left} day${left === 1 ? "" : "s"} left`} · valid until {fmtDate(q.valid_until)}
      </span>
    </div>
  );
}

function QuoteGroup({ group, filter }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const primary = group.primary;
  const older = group.rest;
  const days = daysSince(primary.sent_at);
  const stale = primary.status === "sent" && days !== null && days >= 7;
  const needsApproval = primary.totals?.approval?.required && !primary.approved;
  const oppLink = `/opportunities/${primary.opportunity_id}?tab=quote`;

  return (
    <div className="relative" data-testid="quotes-row">
      {stale && <span className="absolute bottom-4 left-0 top-4 w-[3px] rounded-r-full bg-primary" aria-hidden="true" />}
      <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:gap-5">
        <div className="flex min-w-0 flex-1 items-start gap-4">
          <CompanyLogo name={primary.account_name} domain={primary.account_website} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              <Link to={oppLink} className="text-[14.5px] font-semibold tracking-[-0.01em] hover:text-primary" data-testid="quotes-row-title">
                {primary.account_name} <span className="font-medium text-muted-foreground">/ {cleanTitle(primary)}</span>
              </Link>
              <span className="rounded-md border border-black/[0.08] bg-white px-1.5 py-px font-mono text-[10.5px] font-medium tabular text-muted-foreground">v{primary.version}</span>
              <StatusChip status={primary.status} label={primary.status === "sent" ? "Awaiting decision" : undefined} />
              {needsApproval ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--status-warn)/0.3)] bg-[hsl(var(--status-warn)/0.08)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--status-warn))]" data-testid="quotes-row-approval">
                  <AlertTriangle className="h-3 w-3" /> {primary.totals.approval.label || "Approval needed"}
                </span>
              ) : null}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
              {primary.stage ? <StageBadge stage={primary.stage} className="h-5 px-2 py-0 text-[11px]" /> : null}
              <span>{primary.owner_name}</span>
              {primary.sent_at ? (
                <span className="inline-flex items-center gap-1.5">
                  <Send className="h-3 w-3" /> Sent {fmtDate(primary.sent_at)}
                  {stale ? <span className="font-medium text-primary">· {days} days without a decision</span> : null}
                </span>
              ) : (
                <span>Drafted {relTime(primary.created_at)}</span>
              )}
              {primary.views ? <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> {primary.views} view{primary.views === 1 ? "" : "s"}</span> : null}
            </div>
            <ValidityBar q={primary} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 pl-14 sm:flex-col sm:items-end sm:gap-2 sm:pl-0">
          <div className="text-right">
            <div className="text-[15px] font-semibold tabular tracking-[-0.01em]" data-testid="quotes-row-value">{fmtNOK(primary.totals?.total_sales)} NOK</div>
            <div className={cn("text-[12px] tabular", primary.totals?.gm_pct < 30 ? "text-[hsl(var(--status-warn))]" : "text-muted-foreground")}>GM {fmtPct(primary.totals?.gm_pct)}</div>
          </div>
          <div className="flex items-center gap-1.5">
            {stale ? (
              <Button size="sm" className="gap-1.5" onClick={() => navigate(oppLink)} data-testid="quotes-row-open-link">Follow up <ArrowUpRight className="h-3.5 w-3.5" /></Button>
            ) : (
              <Button size="sm" variant="outline" className="gap-1.5" asChild><Link to={oppLink} data-testid="quotes-row-open-link">Open <ArrowUpRight className="h-3.5 w-3.5" /></Link></Button>
            )}
            <Button variant="ghost" size="icon-sm" asChild title="Open customer view">
              <a href={`/q/${primary.token}`} target="_blank" rel="noreferrer" data-testid="quotes-row-public-link" aria-label="Open customer view"><ExternalLink className="h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      </div>

      {older.length > 0 && (
        <div className="px-5 pb-3 sm:pl-[76px]">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
            data-testid="quotes-row-history-toggle"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {older.length} earlier version{older.length === 1 ? "" : "s"}
          </button>
          {expanded && (
            <ul className="mt-2 divide-y divide-black/[0.05] rounded-xl border border-black/[0.06] bg-[hsl(40_12%_98.5%)]" data-testid="quotes-row-history">
              {older.map((q) => (
                <li key={q.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3.5 py-2 text-[12.5px]">
                  <span className="font-mono text-[11px] font-medium tabular text-muted-foreground">v{q.version}</span>
                  <StatusChip status={q.status} />
                  <span className="text-muted-foreground">{q.sent_at ? `Sent ${fmtDate(q.sent_at)}` : `Drafted ${fmtDate(q.created_at)}`}</span>
                  <span className="ml-auto tabular font-medium">{fmtNOK(q.totals?.total_sales)} NOK</span>
                  <span className="tabular text-muted-foreground">GM {fmtPct(q.totals?.gm_pct)}</span>
                  <a href={`/q/${q.token}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" aria-label="Open customer view"><ExternalLink className="h-3.5 w-3.5" /></a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setError(null);
    api.get("/quotes").then((r) => setQuotes(r.data)).catch((e) => setError(errMsg(e)));
  };
  useEffect(load, []);

  return <QuotesBody quotes={quotes} error={error} onRetry={load} />;
}

/** The quotes page itself, given its quotes. The page above fetches; static previews (the proposal) pass fixed data. */
export function QuotesBody({ quotes, error, onRetry }) {
  const load = onRetry;
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");

  const counts = useMemo(() => {
    const c = { all: quotes?.length || 0 };
    (quotes || []).forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, [quotes]);

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const matches = (x) => (filter === "all" || x.status === filter) && (!s || `${x.account_name} ${x.title} ${x.opportunity_name} ${x.owner_name}`.toLowerCase().includes(s));
    const byOpp = new Map();
    (quotes || []).forEach((x) => {
      if (!byOpp.has(x.opportunity_id)) byOpp.set(x.opportunity_id, []);
      byOpp.get(x.opportunity_id).push(x);
    });
    const out = [];
    byOpp.forEach((versions) => {
      const sorted = [...versions].sort((a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9) || b.version - a.version);
      const visible = sorted.filter(matches);
      if (!visible.length) return;
      const primary = visible[0];
      const rest = filter === "all" ? sorted.filter((x) => x.id !== primary.id) : visible.slice(1);
      out.push({ id: primary.opportunity_id, primary, rest });
    });
    const activity = (x) => new Date(x.sent_at || x.created_at).getTime();
    out.sort((a, b) => {
      if (sort === "value") return (b.primary.totals?.total_sales || 0) - (a.primary.totals?.total_sales || 0);
      if (sort === "waiting") return (daysSince(b.primary.sent_at) ?? -1) - (daysSince(a.primary.sent_at) ?? -1);
      const ra = STATUS_RANK[a.primary.status] ?? 9;
      const rb = STATUS_RANK[b.primary.status] ?? 9;
      if (ra !== rb) return ra - rb;
      return activity(b.primary) - activity(a.primary);
    });
    return out;
  }, [quotes, filter, q, sort]);

  const sent = (quotes || []).filter((x) => x.status === "sent");
  const outstanding = sent.reduce((a, x) => a + (x.totals?.total_sales || 0), 0);
  const stale = sent.filter((x) => (daysSince(x.sent_at) ?? 0) >= 7).length;
  const expiring = sent.filter((x) => x.valid_until && daysUntil(x.valid_until) !== null && daysUntil(x.valid_until) <= 7).length;
  const avgGm = sent.length ? sent.reduce((a, x) => a + (x.totals?.gm_pct || 0), 0) / sent.length : null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Sales"
        title="Quotes"
        description="Every proposal that is out with a customer, what it is worth and how long it has been waiting."
      />
      {error && <InlineError onRetry={load} testId="quotes-error">{error}</InlineError>}

      {!quotes ? (
        <Skeleton className="h-28 rounded-2xl" />
      ) : (
        <KpiStrip cols={4} testId="quotes-stats">
          <Kpi label="Awaiting decision" value={fmtCompact(outstanding)} sub={`${sent.length} quote${sent.length === 1 ? "" : "s"} with customers`} accent testId="quotes-stat-outstanding" />
          <Kpi label="Needs follow-up" value={stale} sub="sent more than 7 days ago" testId="quotes-stat-stale" />
          <Kpi label="Expiring soon" value={expiring} sub="validity ends within 7 days" testId="quotes-stat-expiring" />
          <Kpi label="Avg. margin" value={avgGm === null ? "–" : fmtPct(avgGm)} sub="on quotes awaiting decision" testId="quotes-stat-total" />
        </KpiStrip>
      )}

      <div className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Segmented items={FILTERS.map((f) => ({ ...f, count: quotes ? counts[f.key] || 0 : undefined }))} value={filter} onChange={setFilter} testIdPrefix="quotes-tab" />
        <div className="flex items-center gap-2">
          <SearchField value={q} onChange={setQ} placeholder="Search customer, quote or owner" className="w-full lg:w-[280px]" testId="quotes-search-input" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-10 w-[160px] rounded-xl border-black/[0.09] bg-white text-[13px]" data-testid="quotes-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most recent</SelectItem>
              <SelectItem value="value">Highest value</SelectItem>
              <SelectItem value="waiting">Longest waiting</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        {!quotes ? (
          <div className="surface divide-y divide-black/[0.05]">{[0, 1, 2, 3].map((i) => <div key={i} className="flex gap-4 px-5 py-4"><Skeleton className="h-10 w-10 rounded-xl" /><div className="flex-1"><Skeleton className="h-4 w-1/2" /><Skeleton className="mt-2 h-3 w-1/3" /></div></div>)}</div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={q ? `No quotes match "${q}"` : filter === "all" ? "No quotes yet" : "Nothing here"}
            description="Quotes are generated inside an opportunity once a solution exists."
            action={<Button asChild><Link to="/pipeline">Open pipeline</Link></Button>}
            testId="quotes-empty"
          />
        ) : (
          <div className="surface divide-y divide-black/[0.05] overflow-hidden" data-testid="quotes-list">
            {groups.map((g) => <QuoteGroup key={g.id} group={g} filter={filter} />)}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
